import type { FeedAssetGroup, FeedAssetWarning, RecoverableFeedAssetWarning } from './types';
import { isShortFormAsset } from './utils';

/**
 * The assets a run produced for one improved asset — what an improve landing in the feed is judged
 * by, since the improved asset comes back among the run's own groups either way.
 */
export const producedAssetIds = (groups: FeedAssetGroup[], assetId: string): string[] =>
  groups
    .flatMap((group) => group.assets)
    .filter((asset) => isShortFormAsset(asset) && asset.originalVariationId === assetId)
    .map((asset) => asset.id);

/**
 * Notices for every asset an improve came back from with nothing to show for it, whatever mode it ran
 * in.
 *
 * What the run produced is read off the result rather than counted in the groups: a card holds earlier
 * runs' results and any lower-scoring assets since promoted into it, none of which this run produced.
 *
 * Only groups a run just returned carry a `lastImproveResult` at all — see
 * `FRAGMENT_SHORT_FORM_ASSET_IMPROVE_RESULT` — so nothing here has to establish that the result being
 * read is about the run that just finished.
 */
export const zeroResultImproveWarnings = (groups: FeedAssetGroup[]): FeedAssetWarning[] =>
  groups
    .flatMap((group) => group.assets)
    .filter(isShortFormAsset)
    .filter((asset) => asset.lastImproveResult?.producedAssetIds.length === 0)
    .map((asset) => ({
      id: asset.id,
      recomposeRequestId: asset.lastImproveResult?.recomposeRequestId,
      eligibleToRetry: Boolean(asset.lastImproveResult?.isEligibleToRetry),
      lowerScoringAvailable: Boolean(asset.lastImproveResult?.lowerScoringResultsAvailable),
      failureReason: asset.lastImproveResult?.failureReason,
      blacklistMatches: asset.lastImproveResult?.blacklistMatches,
      retrying: false,
      promoting: false,
      attempt: 0,
    }));

// A warning that never learned which request it describes — a plain zero result, or one restored
// without an id — has nothing to retry or promote from, because both recovery calls name the improve
// they act on. Shared so the card and its buttons cannot disagree about which warnings are offered
// recovery at all.
export const namesImprove = (
  warning?: FeedAssetWarning | null
): warning is RecoverableFeedAssetWarning => Boolean(warning?.recomposeRequestId);
